import { useState } from "react";

import * as api from "../api/client";
import { MetricCard } from "../components/MetricCard";
import { useApi } from "../hooks/useApi";
import type { BillingObservability } from "../types/api";

/** Human labels for the machine alert kinds returned by the backend. */
const ALERT_LABELS: Record<string, string> = {
  dead_letter_growth: "Dead-letter queue growing",
  stale_webhook: "No recent webhook delivery",
  processing_stuck: "Events stuck mid-processing",
};

function alertLabel(kind: string): string {
  return ALERT_LABELS[kind] ?? kind;
}

function formatAge(hours: number | null): string {
  if (hours === null) return "—";
  if (hours < 1) return `${Math.round(hours * 60)} min ago`;
  return `${hours.toFixed(1)} h ago`;
}

function formatCheckedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function HealthBanner({ data }: { data: BillingObservability }) {
  const issueCount = data.alerts.length;
  if (data.healthy) {
    return (
      <output className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-4">
        <span className="text-2xl">✅</span>
        <div>
          <div className="text-green-800 font-semibold">Billing pipeline healthy</div>
          <div className="text-green-700 text-sm">No issues detected.</div>
        </div>
      </output>
    );
  }
  return (
    <div
      className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4"
      role="alert"
    >
      <span className="text-2xl">⚠️</span>
      <div>
        <div className="text-amber-800 font-semibold">
          {issueCount} issue{issueCount === 1 ? "" : "s"} detected
        </div>
        <div className="text-amber-700 text-sm">See the details below.</div>
      </div>
    </div>
  );
}

export function BillingHealthPage() {
  const { data, loading, error, refetch } = useApi(() => api.getBillingObservability(), []);
  const [refreshing, setRefreshing] = useState(false);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      // Re-run the check on the backend, then re-read through the hook so the
      // page reflects the freshly-computed snapshot.
      await api.refreshBillingObservability();
      refetch();
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Billing Health</h2>
          <p className="text-sm text-slate-500 mt-1">
            Live health of the Stripe webhook and billing-jobs pipeline.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-pulse text-slate-500">Loading billing health…</div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4" role="alert">
          Error: {error}
        </div>
      )}

      {data && !loading && (
        <>
          <HealthBanner data={data} />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <MetricCard label="Dead-letter events" value={data.deadLetterCount} />
            <MetricCard label="Stuck processing" value={data.stuckProcessingCount} />
            <MetricCard label="Last webhook" value={formatAge(data.newestEventAgeHours)} />
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-slate-900">Alerts</h3>
            {data.alerts.length === 0 ? (
              <div className="text-slate-500 text-sm">
                No active alerts — everything is healthy.
              </div>
            ) : (
              <ul className="space-y-3">
                {data.alerts.map((alert) => (
                  <li
                    key={alert.kind}
                    className="bg-white border border-amber-200 rounded-lg p-4 shadow-sm"
                  >
                    <div className="font-medium text-slate-900">{alertLabel(alert.kind)}</div>
                    <div className="text-sm text-slate-600 mt-1">{alert.message}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="text-xs text-slate-400">
            Last checked: {formatCheckedAt(data.checkedAt)}
          </div>
        </>
      )}
    </div>
  );
}
